import { useState } from 'react';
import { CalendarClock, Check, ChevronsUpDown } from 'lucide-react';
import { CampaignFormSectionCard } from '@/components/campaigns/campaign-form-section-card';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useI18nContext } from '@/i18n/i18n-react';
import { cn } from '@/lib/utils';

export interface CampaignScheduleSectionProps {
  launchDate: Date | undefined;
  launchTime: string;
  timezone: string;
  launchDateError?: string;
  launchTimeError?: string;
  onDateChange: (date: Date | undefined) => void;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onTimezoneChange: (timezone: string) => void;
  className?: string;
}

export function CampaignScheduleSection({
  launchDate,
  launchTime,
  timezone,
  launchDateError,
  launchTimeError,
  onDateChange,
  onChange,
  onBlur,
  onTimezoneChange,
  className,
}: CampaignScheduleSectionProps) {
  const { LL } = useI18nContext();
  const [isTimezoneOpen, setIsTimezoneOpen] = useState(false);

  return (
    <CampaignFormSectionCard
      icon={CalendarClock}
      title={LL.createCampaign.sections.schedule.title()}
      description={LL.createCampaign.sections.schedule.description()}
      className={className}
    >
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
        <div className="shrink-0 space-y-2">
          <Label>{LL.createCampaign.launchDateLabel()}</Label>
          <div
            className={cn(
              'w-fit rounded-md border p-1',
              launchDateError && 'border-red-500'
            )}
          >
            <DatePicker
              value={launchDate}
              onChange={onDateChange}
              fromDate={new Date()}
              toDate={new Date(Date.now() + 2 * 30 * 24 * 60 * 60 * 1000)}
            />
          </div>
          {launchDateError ? (
            <p className="text-sm text-red-500">{launchDateError}</p>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <div className="space-y-2">
            <Label htmlFor="launchTime">
              {LL.createCampaign.launchTimeLabel()}
            </Label>
            <Input
              id="launchTime"
              name="launchTime"
              value={launchTime}
              onChange={onChange}
              onBlur={onBlur}
              placeholder={LL.createCampaign.launchTimePlaceholder()}
              maxLength={5}
              required
              className={launchTimeError ? 'border-red-500' : ''}
            />
            {launchTimeError ? (
              <p className="text-sm text-red-500">{launchTimeError}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="timezone">
              {LL.createCampaign.timezoneLabel()}
            </Label>
            <Popover open={isTimezoneOpen} onOpenChange={setIsTimezoneOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="timezone"
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between"
                >
                  {timezone}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="relative overflow-hidden p-0">
                <Command>
                  <CommandInput className="sticky top-0" />
                  <CommandEmpty>No timezone found.</CommandEmpty>
                  <CommandGroup className="max-h-[300px] overflow-y-scroll">
                    {Intl.supportedValuesOf('timeZone').map((tz) => (
                      <CommandItem
                        key={tz}
                        value={tz}
                        onSelect={(current) => {
                          setIsTimezoneOpen(false);
                          onTimezoneChange(current);
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            timezone === tz ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        {tz}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
    </CampaignFormSectionCard>
  );
}
